import {useQuery, useQueryClient} from "@tanstack/react-query";
import api from "../../utils/api";
import {HyperlanePreset} from "./types";

interface PresetsResponse {
  presets: HyperlanePreset[];
  defaultPresetId: string | null;
}

const PRESETS_KEY = ['hyper-presets'] as const;

const fetchPresets = async (): Promise<PresetsResponse> => {
  const res = await api.get('/hyper/v2/presets');
  return {
    presets: res.data?.presets ?? [],
    defaultPresetId: res.data?.defaultPresetId ?? null,
  };
};

export default function usePresets() {
  const query = useQuery<PresetsResponse>({
    queryKey: PRESETS_KEY as unknown as string[],
    queryFn: fetchPresets,
    staleTime: 1000 * 60 * 5,
  });

  return {
    presets: query.data?.presets ?? [],
    defaultPresetId: query.data?.defaultPresetId ?? null,
    isPending: query.isPending,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function usePresetsMutations() {
  const queryClient = useQueryClient();
  const key = PRESETS_KEY as unknown as string[];

  return {
    addPreset: (preset: HyperlanePreset) => {
      queryClient.setQueryData<PresetsResponse>(key, (prev) => ({
        presets: [...(prev?.presets ?? []), preset],
        defaultPresetId: prev?.defaultPresetId ?? null,
      }));
    },
    removePreset: (id: string) => {
      queryClient.setQueryData<PresetsResponse>(key, (prev) => ({
        presets: (prev?.presets ?? []).filter(p => p.id !== id),
        defaultPresetId: prev?.defaultPresetId === id ? null : prev?.defaultPresetId ?? null,
      }));
    },
    setDefaultPreset: (id: string | null) => {
      queryClient.setQueryData<PresetsResponse>(key, (prev) => ({
        presets: prev?.presets ?? [],
        defaultPresetId: id,
      }));
    },
  };
}
